// stringify.js
// Canonical, prefix-join serializer that preserves AST structure (no collapsing)
// and ALWAYS includes rule-linked constraints in { ... } even when .links is an object.

import $filter from '../filter'; // optional metric schema registry

function toArrayLinks(links) {
  // Normalize links to a plain array of "rule objects" (not rule-nodes)
  if (!links) return [];
  if (Array.isArray(links)) {
    return links.map(l => (l && l.rule ? l.rule : l)).filter(Boolean);
  }
  if (typeof links === 'object') {
    const arr = [];
    for (const k in links) {
      if (!Object.prototype.hasOwnProperty.call(links, k)) continue;
      const v = links[k];
      if (!v || typeof v !== 'object') continue;
      if (v.rule) arr.push(v.rule);
      else if (v.type && v.logic) arr.push(v);
    }
    return arr;
  }
  return [];
}

function isContains(rule) {
  // Treat "contains" as a setting.logic variant
  return !!(rule && rule.setting && String(rule.setting.logic).toLowerCase() === 'contains');
}

// ---- SETTINGS SUPPORT (multi-slot) ----
const SETTING_ORDER = ['logic', 'period', 'range', 'count', 'rating'];

function getSettingSchema(type) {
  const t = ($filter && $filter.type && $filter.type[type]) || {};
  // Accept array (['logic','period']) or object ({ logic:true, period:true })
  const cfg = t.setting;
  if (!cfg) return new Set();
  if (Array.isArray(cfg)) {
    return new Set(cfg.filter(k => SETTING_ORDER.includes(k)));
  }
  return new Set(SETTING_ORDER.filter(k => cfg && cfg[k]));
}

function buildSettingsPart(type, setting) {
  if (!setting) return '';
  const supported = getSettingSchema(type);
  if (!supported.size) return '';

  const tokens = [];
  for (const key of SETTING_ORDER) {
    if (!supported.has(key)) continue;
    const val = setting[key];
    if (val === undefined || val === null) continue;
    tokens.push(String(val));
  }
  return tokens.length ? `[${tokens.join(',')}]` : '';
}

// --- MAIN SERIALIZER ---

function serializeRule(rule, links) {
  if (!rule) return '';

  const metric = rule.type ?? '';

  // SETTINGS (any combination in canonical order, gated by schema)
  const settingsPart = buildSettingsPart(metric, rule.setting);

  // OPERATOR ('e' instead of '=' for compactness)
  let op = (rule.logic && rule.logic.operand != null) ? String(rule.logic.operand) : '=';
  op = op.replace('=', 'e');

  // VALUES
  const v   = rule.logic ? rule.logic.value      : undefined;
  const to  = rule.logic ? rule.logic.to         : undefined;
  const tol = rule.logic ? rule.logic.tolerance  : undefined;

  let values = [];
  if (Array.isArray(v)) values.push(...v.map(String));
  else if (v != undefined) values.push(String(v));
  if (to  != undefined) values.push(String(to));
  if (tol != undefined) values.push(String(tol));

  if (isContains(rule)) {
    values = values.map(s => encodeURIComponent(s));
  }

  let out = `${metric}${settingsPart}${op}[${values.join(',')}]`;

  // LINKS — normalize array/object and emit if any
  const linkRules = toArrayLinks(links);
  if (linkRules.length) {
    const linkStr = linkRules
      .map(l => serializeRule(l, toArrayLinks(l.links)))
      .join(';');
    out += `{${linkStr}}`;
  }

  return out;
}

function isRuleNode(node) {
  return !!(node && node.rule);
}

function isGroupNode(node) {
  return !!(node && !node.rule && Array.isArray(node.rules));
}

// Serialize the content of a node (no join wrapper here)
function serializeContent(node) {
  if (isRuleNode(node)) return serializeRule(node.rule, node.links);
  const children = Array.isArray(node && node.rules) ? node.rules : [];
  return children.map(serializeItem).join(';');
}

// Serialize ONE item with its prefix join.
// No collapsing: if the node is a group with join 'and', we still emit "( ... )".
function serializeItem(node) {
  const join = (node && node.logic ? String(node.logic) : 'and').toLowerCase();

  if (join === 'and') {
    if (isRuleNode(node)) {
      // and + rule → rule token (links included by serializeRule)
      return serializeRule(node.rule, node.links);
    }
    if (isGroupNode(node)) {
      // and + group → always parentheses, regardless of child count
      const inner = (node.rules || []).map(serializeItem).join(';');
      return `(${inner})`;
    }
    return '';
  }

  // Non-AND joins are explicit wrappers
  const inner = serializeContent(node);

  // Preserve distinction: join(rule) vs join((group))
  if (isGroupNode(node)) {
    return `${join}(( ${inner} ))`.replace('(( ', '((').replace(' ))', '))'); // keep spacing tidy
  }
  return `${join}(${inner})`;
}

// --- EXPORT ---

export default {
  /**
   * run(root, opts?)
   *   - root: JSON tree { logic, rules } or a single { rule }
   *   - opts.encode: URI-encode the result (default false)
   */
  run(root, opts = {}) {
    const { encode = false } = opts;

    // Normalize a bare rule at the root to a one-item group
    const rootGroup = (root && root.rule) ? { logic: 'and', rules: [root] } : root;

    // Top-level: sequence of items joined by ';'
    const items = Array.isArray(rootGroup && rootGroup.rules) ? rootGroup.rules : [];
    const expr = items.map(serializeItem).join(';');

    return encode ? encodeURIComponent(expr) : expr;
  }
};
